import { notifyIcon, requestMethod } from "../../../../config";
import envConfig from "../../../../env.config";
import { fetch } from "../../../../shared/services/api.service";
import { checkApiSuccess } from "../../../../utils";
import { getNotification } from "../../../../utils/common";

const WORKFLOW_URL = `${envConfig.BASE_URL.BASE_API_URL}/workflow`;

/**
 * Adds or updates a workflow expression
 * @param {Object} data - Expression data to save
 * @returns {Promise<Object>} API response
 * @author Abhishek Dhillon
 */
export const createWorkflowNodeExpression = async (data) => {
  let requestConfig = {
    method: requestMethod.PUT,
    url: `${WORKFLOW_URL}/updateWorkflowNodeExpression`,
    data: data,
  };

  let response = await fetch(requestConfig, true, true);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data;
    } else {
      getNotification(response.data.message, notifyIcon.ERROR_ICON);
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Adds or updates a task expression
 * @param {Object} data - Expression data to save
 * @returns {Promise<Object>} API response
 * @author Sarthak Arora
 */
export const updateTaskNodeExpression = async (data) => {
  let requestConfig = {
    method: requestMethod.PUT,
    url: `${WORKFLOW_URL}/updateTaskNodeExpression`,
    data: data,
  };

  let response = await fetch(requestConfig, true, true);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data;
    } else {
      getNotification(response.data.message, notifyIcon.ERROR_ICON);
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Gets workflow expression by variable ID
 * @param {Object} params - Parameters including variableId
 * @returns {Promise<Object>} Expression data
 * @author Abhishek Dhillon
 */
export const getWorkflowExpressionByVariableId = async (params) => {
  let requestConfig = {
    method: requestMethod.GET,
    url: `${WORKFLOW_URL}/getWorkflowExpressionByVariableId`,
    params: params,
  };

  let response = await fetch(requestConfig, true, false);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data.data;
    }
  } else {
    return null;
  }
};

/**
 * Gets all workflow expressions for a workflow
 * @param {Object} params - Parameters including workflowId
 * @returns {Promise<Array>} Array of expressions
 * @author Abhishek Dhillon
 */
export const getWorkflowNodeExpression = async (params) => {
  let requestConfig = {
    method: requestMethod.GET,
    url: `${WORKFLOW_URL}/getWorkflowNodeExpression`,
    params: params,
  };

  let response = await fetch(requestConfig, true, false);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data.data;
    } else {
      getNotification(response.data.message, notifyIcon.ERROR_ICON);
      return [];
    }
  } else {
    return [];
  }
};

/**
 * Deletes a workflow expression
 * @param {Object} data - Data including expressionId
 * @returns {Promise<Object>} API response
 * @author Abhishek Dhillon
 */
export const deleteWorkflowExpression = async (data) => {
  let requestConfig = {
    method: requestMethod.DELETE,
    url: `${WORKFLOW_URL}/deleteWorkflowExpression`,
    data: data,
  };

  let response = await fetch(requestConfig, true, true);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data;
    } else {
      getNotification(response.data.message, notifyIcon.ERROR_ICON);
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Deletes a task expression
 * @param {Object} data - Data including expressionId
 * @returns {Promise<Object>} API response
 * @author Sarthak Arora
 */
export const deleteTaskNodeExpression = async (data) => {
  let requestConfig = {
    method: requestMethod.DELETE,
    url: `${WORKFLOW_URL}/deleteTaskNodeExpression`,
    data: data,
  };

  let response = await fetch(requestConfig, true, true);

  if (checkApiSuccess(response)) {
    if (response?.data?.data) {
      return response.data;
    } else {
      getNotification(response?.data?.message, notifyIcon.ERROR_ICON);
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Deletes a workflow expression
 * @param {Object} data - Data including expressionId
 * @returns {Promise<Object>} API response
 * @author Sarthak Arora
 */
export const deleteWorkflowNodeExpression = async (data) => {
  let requestConfig = {
    method: requestMethod.DELETE,
    url: `${WORKFLOW_URL}/deleteWorkflowNodeExpression`,
    data: data,
  };

  let response = await fetch(requestConfig, true, true);

  if (checkApiSuccess(response)) {
    if (response?.data?.data) {
      return response.data;
    } else {
      getNotification(response?.data?.message, notifyIcon.ERROR_ICON);
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Validates a workflow expression
 * @param {Object} data - Expression data to validate
 * @returns {Promise<Object>} Validation result
 * @author Abhishek Dhillon
 */
export const validateWorkflowExpression = async (data) => {
  let requestConfig = {
    method: requestMethod.POST,
    url: `${WORKFLOW_URL}/validateWorkflowExpression`,
    data: data,
  };

  let response = await fetch(requestConfig, true, false);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data.data;
    } else {
      getNotification(response.data.message, notifyIcon.ERROR_ICON);
      return { isValid: false, errors: [response.data.message] };
    }
  } else {
    return { isValid: false, errors: ["Validation request failed"] };
  }
};

/**
 * Tests a workflow expression against test data
 * @param {Object} data - Expression and test data
 * @returns {Promise<Object>} Test result
 * @author Abhishek Dhillon
 */
export const testWorkflowExpression = async (data) => {
  let requestConfig = {
    method: requestMethod.POST,
    url: `${WORKFLOW_URL}/testWorkflowExpression`,
    data: data,
  };

  let response = await fetch(requestConfig, true, false);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data.data;
    } else {
      getNotification(response.data.message, notifyIcon.ERROR_ICON);
      return { result: false, error: response.data.message };
    }
  } else {
    return { result: false, error: "Test request failed" };
  }
};

/**
 * Gets a specific workflow expression by ID
 * @param {Object} params - Parameters including expressionId
 * @returns {Promise<Object>} Expression data
 * @author Abhishek Dhillon
 */
export const getWorkflowExpressionById = async (params) => {
  let requestConfig = {
    method: requestMethod.GET,
    url: `${WORKFLOW_URL}/getWorkflowExpressionById`,
    params: params,
  };

  let response = await fetch(requestConfig, true, false);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data.data;
    } else {
      getNotification(response.data.message, notifyIcon.ERROR_ICON);
      return null;
    }
  } else {
    return null;
  }
};

/**
 * Checks if expression name is unique within workflow
 * @param {Object} params - Parameters including workflowId and name
 * @returns {Promise<boolean>} True if name is unique
 * @author Abhishek Dhillon
 */
export const checkIsExpressionNameUnique = async (params) => {
  let requestConfig = {
    method: requestMethod.GET,
    url: `${WORKFLOW_URL}/checkIsExpressionNameUnique`,
    params: params,
  };

  let response = await fetch(requestConfig, true, false);

  if (checkApiSuccess(response)) {
    if (response.data.data) {
      return response.data.data?.exists;
    } else {
      getNotification(response.data.message, notifyIcon.ERROR_ICON);
      return false;
    }
  } else {
    return false;
  }
};
